import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { getTESASupplementDocumentApi } from "../../../apis/registration";
import {
    isDocumentUnavailableReason,
    type DocumentUnavailableReason,
} from "./document-view.constants";

interface DocumentError {
    message: string;
    reason: DocumentUnavailableReason | null;
}

const FALLBACK_ERROR: DocumentError = { message: 'Unable to load document', reason: null };

function toDocumentError(body: { message?: string | string[]; reason?: unknown }): DocumentError {
    const message = Array.isArray(body.message) ? body.message.join(', ') : body.message;

    return {
        message: message ?? FALLBACK_ERROR.message,
        reason: isDocumentUnavailableReason(body.reason) ? body.reason : null,
    };
}

/**
 * The viewer requests `arraybuffer`, so error bodies arrive as raw bytes rather than parsed JSON
 * and have to be decoded before the reason code is readable.
 */
function extractDocumentError(err: unknown): DocumentError {
    const axiosErr = err as { response?: { data?: unknown; status?: number } };
    const data = axiosErr?.response?.data;

    if (data && typeof data === 'object' && !(data instanceof ArrayBuffer)) {
        return toDocumentError(data as { message?: string | string[]; reason?: unknown });
    }

    if (data instanceof ArrayBuffer) {
        const text = new TextDecoder().decode(data);
        try {
            return toDocumentError(JSON.parse(text) as { message?: string | string[]; reason?: unknown });
        } catch {
            return { message: text || FALLBACK_ERROR.message, reason: null };
        }
    }

    if (err instanceof Error) {
        return { message: err.message, reason: null };
    }

    return FALLBACK_ERROR;
}

export const TESASupplement: React.FC = () => {
    const [objectURL, setObjectURL] = useState<string | undefined>();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<DocumentError | null>(null);
    const { agreementId } = useParams();

    useEffect(() => {
        const controller = new AbortController();

        const init = async (): Promise<void> => {
            try {
                const url = await getTESASupplementDocumentApi(
                    agreementId as string,
                    controller.signal
                );
                if (!controller.signal.aborted) {
                    setObjectURL(url);
                }
            } catch (err: unknown) {
                if (controller.signal.aborted) return;
                setError(extractDocumentError(err));
            } finally {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            }
        };

        void init();
        return () => controller.abort();
    }, [agreementId]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen bg-slate-50 dark:bg-slate-900">
                <div className="text-center">
                    <svg className="animate-spin h-8 w-8 text-indigo-500 dark:text-indigo-400 mx-auto mb-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    <p className="text-slate-500 dark:text-slate-400 text-sm">Loading document...</p>
                </div>
            </div>
        );
    }

    if (error) {
        // A known reason code means the document is simply not stored yet, which is an expected
        // state rather than a failure, so it gets the softer amber treatment.
        const isInfo = error.reason !== null;

        return (
            <div className="flex items-center justify-center h-screen bg-slate-50 dark:bg-slate-900">
                <div className="text-center max-w-md p-6">
                    <svg
                        className={`h-8 w-8 mx-auto mb-3 ${isInfo ? 'text-amber-400' : 'text-red-400'}`}
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                    </svg>
                    <p className="text-slate-600 dark:text-slate-400 text-sm mb-2">
                        {isInfo ? 'Document not available yet' : 'Unable to load document'}
                    </p>
                    <p className="text-slate-400 dark:text-slate-500 text-xs">{error.message}</p>
                </div>
            </div>
        );
    }

    return <iframe className="h-screen bg-slate-50 dark:bg-slate-900" height="100%" width="100%" src={objectURL} />
};

export default TESASupplement;
